/*!

=========================================================
* Argon Dashboard React - v1.2.4
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import AppContext from "AppContext";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Row,
  Col,
} from "reactstrap";

import logo from 'assets/img/brand/logo-viatech.png'
import { validateEmail } from "services/Utils";
import Error from "components/Error";
import { alert } from "components/Alert";
import { erroInesperado } from "services/Utils";
import { showLoading } from "components/Loading";
import { hideLoading } from "components/Loading";
import authService from "services/AuthService";
import SelecionarPapel from "components/SelecionarPapel";

const Login = () => {
  const [data, setData] = useState({})
  const [errors, setErrors] = useState([])
  // SDD 034 - desafio de selecao de papel (POST /auth devolve preAuthToken + papeis quando o
  // usuario tem mais de 1 vinculo de site, ver Decisao de escopo 3 da SDD 034/Decisao 5 da SDD 033)
  const [papeis, setPapeis] = useState(null)
  const [preAuthToken, setPreAuthToken] = useState(null)
  const { state, actions } = useContext(AppContext)
  const navigate = useNavigate()

  useEffect(() => {
    if (state.user) {
      navigate('/admin')
    }
  }, [state])

  const validar = () => {
    const errors = []
    if (!data?.email) {
      errors.push({
        fieldName: 'email',
        error: 'Campo obrigatório'
      })
    } else if (!validateEmail(data?.email)) {
      errors.push({
        fieldName: 'email',
        error: 'E-mail inválido'
      })
    }
    setErrors(errors)
    return errors.length === 0
  }

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value })
  }

  const handleSubmit = async (ev) => {
    ev.preventDefault()

    if (validar()) {
      let res = null
      try {
        showLoading()
        res = await authService.login(data.email, data.senha)
        // SDD 034 (Decisao de escopo 3) - POST /auth muda de shape conforme a quantidade de
        // vinculos de site do usuario: { token } (fluxo direto, igual a hoje) ou
        // { preAuthToken, papeis } (desafio - abre o modal de selecao).
        if (res.success && res.data?.token) {
          // 1 unico papel de site: nao ha lista para trocar de papel
          actions.setToken(res.data.token, [])
        } else if (res.success && res.data?.preAuthToken) {
          setPreAuthToken(res.data.preAuthToken)
          setPapeis(res.data.papeis)
        } else {
          alert({
            header: 'Atenção',
            text: 'Dados de acesso inválidos'
          })
        }
        hideLoading()
      } catch (ex) {
        hideLoading()
        if (ex.message == 'Unauthorized') {
          alert({
            header: 'Atenção',
            text: 'Dados de acesso inválidos'
          })
        } else {
          erroInesperado()
        }
      }
    }
  }

  const handleSelecionarPapel = async (idUsuarioPerfil) => {
    try {
      showLoading()
      const res = await authService.selecionarPapel(preAuthToken, idUsuarioPerfil)
      if (res.success) {
        const papeisSelecionados = papeis
        setPapeis(null)
        setPreAuthToken(null)
        await actions.setToken(res.data.token, papeisSelecionados)
      } else {
        alert({
          header: 'Atenção',
          text: 'Dados de acesso inválidos'
        })
      }
      hideLoading()
    } catch (ex) {
      hideLoading()
      if (ex.message == 'Unauthorized') {
        // preAuthToken expirado (5 min) - volta pra tela de login pra tentar de novo
        setPapeis(null)
        setPreAuthToken(null)
        alert({
          header: 'Atenção',
          text: 'A seleção de papel expirou, faça login novamente'
        })
      } else {
        erroInesperado()
      }
    }
  }

  const handleResetarSenha = async (e) => {
    e.preventDefault()

    if (!data.email) {
      alert({
        header: 'Atenção',
        text: 'Informe o e-mail para redefinir a senha'
      })
      return
    }

    if (!validateEmail(data?.email)) {
      alert({
        header: 'Atenção',
        text: 'E-mail inválido'
      })
      return
    }

    try {
      await authService.solicitarCodigoResetSenha(data.email)
      alert({
        header: 'Alteração de Senha',
        text: 'Um e-mail foi enviado com um link para criação de uma nova senha, o link tem validade de 15 minutos.'
      })
    } catch (ex) {

    }

  }

  return (
    <>
      <Col lg="5" md="7">
        <Card className="bg-secondary shadow border-0">
          <CardBody className="px-lg-5 py-lg-5">
            <div className="mb-4 text-center">
              <img src={logo} style={{ width: 200 }} alt="VIATECH" />
            </div>
            <div className="text-center text-muted mb-4">
              <small>Identifique-se</small>
            </div>
            <Form role="form" onSubmit={handleSubmit}>
              <FormGroup className="mb-3">
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-email-83" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Email"
                    type="email"
                    autoComplete="new-email"
                    value={data.email}
                    onChange={e => handleChange('email', e.target.value)}
                  />
                </InputGroup>
                <Error errors={errors} fieldName="email" className="mt-1" />
              </FormGroup>
              <FormGroup>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-lock-circle-open" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Senha"
                    type="password"
                    autoComplete="new-password"
                    value={data.senha}
                    onChange={e => handleChange('senha', e.target.value)}
                  />
                </InputGroup>
              </FormGroup>
              <Row className="align-items-center">
                <Col>
                  <a href="lembrar_senha" onClick={handleResetarSenha}><small>Esqueci a senha</small></a>
                </Col>
                <Col className="text-right">
                  <Button className="my-2" color="primary" type="submit">
                    Entrar
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <SelecionarPapel isOpen={!!papeis} papeis={papeis} onSelecionar={handleSelecionarPapel} />
    </>
  );
};

export default Login;
